import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Box, Checkbox } from '@mui/material';
import api from '../services/api';

const DOW_OPTS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

const FREQ_OPTS = ['SEMANAL', 'BISEMANAL', 'ODD_WEEKS', 'EVEN_WEEKS'];

function niceDate(d) {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const x = new Date(s);
  return isNaN(x.getTime()) ? s : x.toISOString().slice(0, 10);
}

export default function OrdenesFijasHubModal({
  open,
  onClose,
  selectedDetalleIds = [],
  defaultCliente = '',
  defaultCarguera = '',
  onProgramarSuccess,
  onSubirSuccess,
  onOpenFactura
}) {
  const [tab, setTab] = useState('subir'); // "programar" | "subir" | "gestionar"
  const [loading, setLoading] = useState(false);

  // ---------- PROGRAMAR ----------
  const [formProg, setFormProg] = useState({
    Idcliente: defaultCliente || '',
    idcarguera: defaultCarguera || '',
    dia_semana: 1,
    frecuencia: 'SEMANAL',
    fecha_inicio: '',
    fecha_fin: '',
    lead_time_dias: 3,
    observaciones: '',
    esramo: 0
  });

  useEffect(() => {
    if (open) {
      setFormProg((f) => ({
        ...f,
        Idcliente: defaultCliente || '',
        idcarguera: defaultCarguera || ''
      }));
    }
  }, [open, defaultCliente, defaultCarguera]);

  const disabledProg = useMemo(
    () =>
      !formProg.Idcliente ||
      !formProg.idcarguera ||
      !formProg.dia_semana ||
      selectedDetalleIds.length === 0 ||
      loading,
    [formProg, selectedDetalleIds, loading]
  );

  const handleProg = (e) => {
    const { name, value } = e.target;
    setFormProg((f) => ({ ...f, [name]: value }));
  };

  const submitProgramar = async (e) => {
    e?.preventDefault();
    if (disabledProg) return;
    try {
      setLoading(true);
      const payload = {
        Idcliente: Number(formProg.Idcliente),
        idcarguera: Number(formProg.idcarguera),
        dia_semana: Number(formProg.dia_semana),
        frecuencia: formProg.frecuencia,
        fecha_inicio: formProg.fecha_inicio || null,
        fecha_fin: formProg.fecha_fin || null,
        lead_time_dias: Number(formProg.lead_time_dias || 0),
        observaciones: formProg.observaciones || null,
        iddetalle_list: selectedDetalleIds,
        esramo_all: Number(formProg.esramo || 0)
      };
      const { data } = await api.post('/api/ordenes-fijas/from-factura', payload);
      if (data?.ok) {
        onProgramarSuccess?.(data);
        onClose?.();
      } else {
        alert(data?.msg || 'No se pudo programar.');
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.msg || err.message || 'Error al programar');
    } finally {
      setLoading(false);
    }
  };

  // ---------- SUBIR ----------
  const [fechaSubir, setFechaSubir] = useState(() => new Date().toISOString().slice(0, 10));
  const [resultadoSubir, setResultadoSubir] = useState(null);
  const [selSubir, setSelSubir] = useState(new Set());

  // PREVIEW de esa fecha
  const [preLoading, setPreLoading] = useState(false);
  const [preview, setPreview] = useState({ ok: true, items: [] });

  const fetchPreview = async (fecha) => {
    try {
      setPreLoading(true);
      const { data } = await api.get('/api/ordenes-fijas/preview', { params: { fecha } });
      setPreview(data);
      setSelSubir(new Set((data?.items || []).map((item) => Number(item.id))));
      setResultadoSubir(null);
    } catch (e) {
      console.error('Preview error:', e);
      setPreview({ ok: false, items: [], msg: e?.response?.data?.msg || e.message });
      setSelSubir(new Set());
    } finally {
      setPreLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (tab === 'subir') fetchPreview(fechaSubir);
  }, [open, tab, fechaSubir]);

  const submitSubir = async (e) => {
    e?.preventDefault();
    const orden_fija_ids = Array.from(selSubir).map(Number).filter(Boolean);
    if (orden_fija_ids.length === 0) {
      alert('Selecciona al menos una orden fija para subir.');
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.post('/api/ordenes-fijas/subir', {
        fecha: fechaSubir,
        orden_fija_ids
      });
      setResultadoSubir(data);
      onSubirSuccess?.(data);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.msg || err.message || 'Error al subir programadas');
    } finally {
      setLoading(false);
    }
  };

  const previewItems = preview?.items || [];
  const selectedSubirCount = selSubir.size;
  const allSubirSelected =
    previewItems.length > 0 && previewItems.every((item) => selSubir.has(Number(item.id)));

  const toggleSubir = (id, checked) => {
    const nId = Number(id);
    setSelSubir((prev) => {
      const next = new Set(prev);
      if (checked) next.add(nId);
      else next.delete(nId);
      return next;
    });
  };

  const toggleAllSubir = (checked) => {
    setSelSubir(checked ? new Set(previewItems.map((item) => Number(item.id))) : new Set());
  };

  // ---------- GESTIONAR (listar/seleccionar/eliminar) ----------
  const [plLoading, setPlLoading] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [selPlantillas, setSelPlantillas] = useState(new Set());
  const [searchPl, setSearchPl] = useState('');

  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selJobs, setSelJobs] = useState(new Set());
  const [searchJobs, setSearchJobs] = useState('');

  // limpiar selección al cambiar de pestaña
  useEffect(() => {
    if (tab !== 'gestionar') {
      setSelPlantillas(new Set());
      setSelJobs(new Set());
    }
  }, [tab]);

  const fetchPlantillas = async () => {
    try {
      setPlLoading(true);
      const { data } = await api.get('/api/ordenes-fijas/list');
      setPlantillas(data?.items || []);
    } catch (e) {
      console.error(e);
      setPlantillas([]);
    } finally {
      setPlLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const { data } = await api.get('/api/ordenes-fijas/jobs'); // trae todos
      setJobs(data?.items || []);
    } catch (e) {
      console.error(e);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (tab === 'gestionar') {
      fetchPlantillas();
      fetchJobs();
    }
  }, [open, tab]);

  const eliminarPlantillas = async () => {
    if (!selPlantillas.size) return alert('Selecciona al menos una orden fija.');
    if (
      !window.confirm(
        '¿Eliminar la(s) orden(es) fija(s) seleccionada(s)? Se borrarán también sus detalles y programaciones (jobs).'
      )
    )
      return;
    try {
      const ids = Array.from(selPlantillas);
      const { data } = await api.delete('/api/ordenes-fijas', { data: { ids } });
      if (data?.ok) {
        await fetchPlantillas();
        setSelPlantillas(new Set());
        alert(`✅ Eliminadas: ${data.borradas}`);
      } else {
        alert(data?.msg || 'No se pudo eliminar.');
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.msg || e.message || 'Error al eliminar');
    }
  };

  const eliminarProgramaciones = async () => {
    if (!selJobs.size) return alert('Selecciona al menos una programación.');
    if (
      !window.confirm(
        '¿Eliminar la(s) programación(es) seleccionada(s)? Esto permite re-subir esa fecha nuevamente.'
      )
    )
      return;
    try {
      const job_ids = Array.from(selJobs);
      const { data } = await api.delete('/api/ordenes-fijas/jobs', { data: { job_ids } });
      if (data?.ok) {
        await fetchJobs();
        setSelJobs(new Set());
        alert(`✅ Programaciones eliminadas: ${data.eliminados}`);
      } else {
        alert(data?.msg || 'No se pudo eliminar.');
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.msg || e.message || 'Error al eliminar');
    }
  };

  // Ver PDF de PLANTILLA (cuando hay exactamente 1 seleccionada)
  const verPdfPlantilla = () => {
    const ids = Array.from(selPlantillas);
    if (ids.length !== 1) return;
    const id = ids[0];
    window.open(`/api/ordenes-fijas/plantilla/${id}/pdf`, '_blank');
  };

  // ---------- EDITAR PLANTILLA ----------
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSavingHeader, setEditSavingHeader] = useState(false);
  const [editHeader, setEditHeader] = useState(null);
  const [editRows, setEditRows] = useState([]);
  const [editSelection, setEditSelection] = useState([]);
  const [catalogoEdit, setCatalogoEdit] = useState([]);
  const [proveedoresEdit, setProveedoresEdit] = useState([]);
  const [clientesEdit, setClientesEdit] = useState([]);

  const toNum = useCallback((v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  }, []);

  const fmtUSD = useCallback(
    (v) =>
      '$' +
      toNum(v, 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    [toNum]
  );

  const getDetalleRowId = useCallback((row) => row?.iddetalle ?? row?.id, []);

  const normalizarSelectionModel = useCallback((model) => {
    if (Array.isArray(model)) {
      return model.map(Number).filter((n) => Number.isFinite(n));
    }

    if (model?.ids instanceof Set) {
      return Array.from(model.ids)
        .map(Number)
        .filter((n) => Number.isFinite(n));
    }

    return [];
  }, []);

  const editRowSelectionModel = useMemo(
    () => ({
      type: 'include',
      ids: new Set(editSelection)
    }),
    [editSelection]
  );

  const handleEditSelectionChange = useCallback(
    (model) => {
      setEditSelection(normalizarSelectionModel(model));
    },
    [normalizarSelectionModel]
  );

  const cargarCatalogosEdicion = useCallback(async () => {
    const [catRes, provRes, cliRes] = await Promise.all([
      api.get('/api/catalogo/todo'),
      api.get('/api/terceros?tipo=proveedor'),
      api.get('/api/terceros?tipo=cliente')
    ]);

    setCatalogoEdit(catRes.data || []);
    setProveedoresEdit(provRes.data || []);
    setClientesEdit(cliRes.data || []);
  }, []);

  useEffect(() => {
    if (!open || tab !== 'programar') return;
    cargarCatalogosEdicion().catch((e) => console.error('Error cargando catálogos:', e));
  }, [open, tab, cargarCatalogosEdicion]);

  const abrirEditarPlantilla = async () => {
    const ids = Array.from(selPlantillas);
    if (ids.length !== 1) {
      alert('Selecciona una sola orden fija para editar.');
      return;
    }

    const id = ids[0];
    try {
      setEditLoading(true);
      setEditOpen(true);
      await cargarCatalogosEdicion();

      const { data } = await api.get(`/api/ordenes-fijas/plantilla/${id}/full`);
      if (!data?.ok) throw new Error(data?.msg || 'No se pudo cargar la orden fija');

      setEditHeader({
        ...data.header,
        fecha_inicio: niceDate(data.header?.fecha_inicio),
        fecha_fin: niceDate(data.header?.fecha_fin)
      });
      setEditRows((data.detalle || []).map((r) => ({ ...r, id: r.iddetalle ?? r.id })));
      setEditSelection([]);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.msg || e.message || 'Error al cargar la orden fija');
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const cerrarEditarPlantilla = async () => {
    setEditOpen(false);
    setEditHeader(null);
    setEditRows([]);
    setEditSelection([]);
    await fetchPlantillas();
    await fetchJobs();
  };

  const handleEditHeader = (e) => {
    const { name, value } = e.target;
    setEditHeader((h) => ({ ...h, [name]: value }));
  };

  const guardarEncabezadoEdit = async () => {
    if (!editHeader?.id) return;

    if (!editHeader.Idcliente || !editHeader.idcarguera || !editHeader.dia_semana) {
      alert('Cliente, carguera y día son obligatorios.');
      return;
    }

    if (
      editHeader.fecha_inicio &&
      editHeader.fecha_fin &&
      editHeader.fecha_fin < editHeader.fecha_inicio
    ) {
      alert('La fecha fin no puede ser menor a la fecha inicio.');
      return;
    }

    try {
      setEditSavingHeader(true);
      const payload = {
        Idcliente: Number(editHeader.Idcliente),
        idcarguera: Number(editHeader.idcarguera),
        estado: editHeader.estado || 'proceso',
        tipoMovimiento: editHeader.tipoMovimiento || 'C',
        tipoDocumento: editHeader.tipoDocumento || 'F',
        dia_semana: Number(editHeader.dia_semana),
        frecuencia: editHeader.frecuencia || 'SEMANAL',
        fecha_inicio: editHeader.fecha_inicio || null,
        fecha_fin: editHeader.fecha_fin || null,
        lead_time_dias: Number(editHeader.lead_time_dias || 0),
        observaciones: editHeader.observaciones || null
      };

      const { data } = await api.put(
        `/api/ordenes-fijas/plantilla/${editHeader.id}/header`,
        payload
      );
      if (!data?.ok) throw new Error(data?.msg || 'No se pudo guardar encabezado');
      alert('✅ Encabezado guardado');
      await fetchPlantillas();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.msg || e.message || 'Error al guardar encabezado');
    } finally {
      setEditSavingHeader(false);
    }
  };

  const agregarDetalleEdit = async () => {
    if (!editHeader?.id) return;
    try {
      const { data } = await api.post(`/api/ordenes-fijas/plantilla/${editHeader.id}/detalle`, {
        codigo: '',
        idproducto: 25,
        cantidad: 1,
        piezas: 1,
        cantidadRamos: 0,
        precio_unitario: 0,
        precio_venta: 0,
        esramo: 0
      });
      if (!data?.ok) throw new Error(data?.msg || 'No se pudo agregar línea');

      const recarga = await api.get(`/api/ordenes-fijas/plantilla/${editHeader.id}/full`);
      setEditRows((recarga.data?.detalle || []).map((r) => ({ ...r, id: r.iddetalle ?? r.id })));
      await fetchPlantillas();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.msg || e.message || 'Error al agregar línea');
    }
  };

  const eliminarDetalleEdit = async () => {
    const ids = Array.from(editSelection || [])
      .map(Number)
      .filter(Boolean);
    if (!ids.length) return alert('Selecciona al menos una línea del detalle.');
    if (!window.confirm(`¿Eliminar ${ids.length} línea(s) de esta orden fija?`)) return;

    try {
      for (const id of ids) {
        await api.delete(`/api/ordenes-fijas/plantilla-detalle/${id}`);
      }
      setEditRows((prev) => prev.filter((r) => !ids.includes(Number(getDetalleRowId(r)))));
      setEditSelection([]);
      await fetchPlantillas();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.msg || e.message || 'Error al eliminar líneas');
    }
  };

  const processEditRowUpdate = useCallback(async (newRow, oldRow) => {
    const id = getDetalleRowId(newRow);
    if (!id) return oldRow;

    const payload = {
      codigo: newRow.codigo || '',
      idproveedor: newRow.idproveedor || null,
      idproducto: newRow.idproducto || null,
      idvariedad: newRow.idvariedad || null,
      idlongitud: newRow.idlongitud || null,
      idempaque: newRow.idempaque || null,
      idtipocaja: newRow.idtipocaja || null,
      idgrupo: newRow.idgrupo || null,
      cantidad: toNum(newRow.cantidad, 0),
      piezas: toNum(newRow.piezas, 0),
      cantidadRamos: toNum(newRow.cantidadRamos, 0),
      precio_unitario: toNum(newRow.precio_unitario, 0),
      precio_venta: toNum(newRow.precio_venta, 0),
      idusuario: newRow.idusuario || null,
      idmix: newRow.idmix || null,
      esramo: newRow.esramo === 1 || newRow.esramo === true || newRow.esramo === '1' ? 1 : 0
    };

    const { data } = await api.put(`/api/ordenes-fijas/plantilla-detalle/${id}`, payload);
    if (!data?.ok) throw new Error(data?.msg || 'No se pudo actualizar la línea');

    const updated = { ...newRow, ...(data.detalle || {}), id };
    setEditRows((prev) =>
      prev.map((r) => (Number(getDetalleRowId(r)) === Number(id) ? updated : r))
    );
    return updated;
  }, [getDetalleRowId, toNum]);

  const optionsCatalogo = useCallback(
    (categoria) =>
      catalogoEdit
        .filter((c) => c.categoria === categoria)
        .sort((a, b) =>
          String(a.valor).localeCompare(String(b.valor), undefined, {
            numeric: true,
            sensitivity: 'base'
          })
        )
        .map((c) => ({ value: Number(c.id), label: String(c.valor) })),
    [catalogoEdit]
  );

  const clientesOpciones = useMemo(
    () =>
      (clientesEdit || [])
        .slice()
        .sort((a, b) =>
          String(a.nombre || '').localeCompare(String(b.nombre || ''), undefined, {
            numeric: true,
            sensitivity: 'base'
          })
        )
        .map((c) => ({ value: Number(c.idtercero), label: String(c.nombre || '') })),
    [clientesEdit]
  );

  const carguerasOpciones = useMemo(
    () => optionsCatalogo('carguera'),
    [optionsCatalogo]
  );

  const columnsEditDetalle = useMemo(
    () => [
      {
        field: 'iddetalle',
        headerName: 'No.',
        width: 70,
        sortable: false,
        renderCell: (params) => params.row?.iddetalle ?? params.row?.id ?? ''
      },
      { field: 'codigo', headerName: 'Mark', width: 90, editable: true },
      {
        field: 'idproveedor',
        headerName: 'Proveedor',
        width: 150,
        editable: true,
        type: 'singleSelect',
        valueOptions: proveedoresEdit.map((p) => ({ value: Number(p.idtercero), label: p.nombre }))
      },
      {
        field: 'idproducto',
        headerName: 'Producto',
        width: 120,
        editable: true,
        type: 'singleSelect',
        valueOptions: optionsCatalogo('producto')
      },
      {
        field: 'idvariedad',
        headerName: 'Variedad',
        width: 150,
        editable: true,
        type: 'singleSelect',
        valueOptions: optionsCatalogo('variedad')
      },
      {
        field: 'idlongitud',
        headerName: 'Lon.',
        width: 80,
        editable: true,
        type: 'singleSelect',
        valueOptions: optionsCatalogo('longitud')
      },
      { field: 'piezas', headerName: 'Piezas', width: 85, editable: true, type: 'number' },
      {
        field: 'idtipocaja',
        headerName: 'Tip.',
        width: 90,
        editable: true,
        type: 'singleSelect',
        valueOptions: optionsCatalogo('tipocaja')
      },
      { field: 'cantidad', headerName: 'Cant.', width: 80, editable: true, type: 'number' },
      { field: 'cantidadRamos', headerName: 'Ramos', width: 90, editable: true, type: 'number' },
      {
        field: 'idempaque',
        headerName: 'Emp.',
        width: 90,
        editable: true,
        type: 'singleSelect',
        valueOptions: optionsCatalogo('empaque')
      },
      {
        field: 'esramo',
        headerName: 'Es Ramo',
        width: 90,
        sortable: false,
        filterable: false,
        editable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          const checked =
            params.row?.esramo === 1 || params.row?.esramo === '1' || params.row?.esramo === true;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <Checkbox
                checked={checked}
                size="small"
                onChange={async (e) => {
                  const updated = { ...params.row, esramo: e.target.checked ? 1 : 0 };
                  await processEditRowUpdate(updated, params.row);
                }}
              />
            </Box>
          );
        }
      },
      {
        field: 'precio_unitario',
        headerName: 'P.Compra',
        width: 100,
        editable: true,
        type: 'number'
      },
      { field: 'precio_venta', headerName: 'P.Venta', width: 100, editable: true, type: 'number' },
      { field: 'totalRamos', headerName: 'T.Ramos', width: 90, type: 'number' },
      { field: 'cantidadTallos', headerName: 'Tallos', width: 90, type: 'number' },
      {
        field: 'subtotal',
        headerName: 'Subtotal',
        width: 110,
        type: 'number',
        renderCell: (params) => fmtUSD(params.value)
      },
      {
        field: 'subtotalVenta',
        headerName: 'SubVenta',
        width: 110,
        type: 'number',
        renderCell: (params) => fmtUSD(params.value)
      }
    ],
    [fmtUSD, optionsCatalogo, processEditRowUpdate, proveedoresEdit]
  );

  // ---------- estilos ----------
  const overlay = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  };
  const card = {
    background: '#fff',
    padding: 20,
    borderRadius: 12,
    width: 'min(1520px, 98vw)',
    maxHeight: '94vh',
    overflow: 'auto',
    boxShadow: '0 10px 30px rgba(0,0,0,.2)',
    color: '#111'
  };
  const tabsWrap = {
    background: '#eef2ff',
    padding: 6,
    borderRadius: 10,
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap'
  };
  const tabBtn = (active) => ({
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid ' + (active ? '#2563eb' : '#d1d5db'),
    background: active ? '#2563eb' : '#fff',
    color: active ? '#fff' : '#111',
    fontWeight: 700,
    minWidth: 150,
    cursor: 'pointer'
  });
  const grid2 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 12
  };
  const fieldStyle = {
    width: '100%',
    padding: 8,
    minHeight: 40,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    boxSizing: 'border-box'
  };
  const sectionHeader = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 8
  };
  const actionsGroup = {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap'
  };
  const actionButton = {
    padding: '9px 14px',
    borderRadius: 8,
    border: 0,
    minWidth: 96,
    whiteSpace: 'nowrap'
  };

  const filtroPl = plantillas.filter((p) => {
    const q = searchPl.trim().toLowerCase();
    if (!q) return true;
    return [p.cliente, p.Idcliente, p.observaciones, p.id].some((x) =>
      String(x || '')
        .toLowerCase()
        .includes(q)
    );
  });

  const filtroJobs = jobs.filter((j) => {
    const q = searchJobs.trim().toLowerCase();
    if (!q) return true;
    return [j.cliente, j.Idcliente, j.observaciones, j.orden_fija_id, j.id].some((x) =>
      String(x || '')
        .toLowerCase()
        .includes(q)
    );
  });

  return !open ? null : (
    <div style={overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={card}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
            Órdenes fijas
          </h3>
          <button onClick={onClose} style={{ border: 0, background: 'transparent', fontSize: 18 }}>
            ✕
          </button>
        </div>

        <div style={tabsWrap}>
          <button
            style={tabBtn(tab === 'programar')}
            onClick={() => setTab('programar')}
            type="button"
          >
            Programar desde factura
          </button>
          <button style={tabBtn(tab === 'subir')} onClick={() => setTab('subir')} type="button">
            Subir programadas
          </button>
          <button
            style={tabBtn(tab === 'gestionar')}
            onClick={() => setTab('gestionar')}
            type="button"
          >
            Gestionar
          </button>
        </div>

        {/* ---------- PROGRAMAR ---------- */}
        {tab === 'programar' && (
          <form onSubmit={submitProgramar} style={{ marginTop: 14 }}>
            <div style={{ color: '#667085', fontSize: 12, marginBottom: 12 }}>
              Seleccionadas: <b>{selectedDetalleIds.length}</b> filas de la factura.
            </div>
            <div style={grid2}>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Cliente</div>
                <select
                  name="Idcliente"
                  value={formProg.Idcliente}
                  onChange={handleProg}
                  style={fieldStyle}
                >
                  <option value="">-- Selecciona cliente --</option>
                  {formProg.Idcliente &&
                    !clientesOpciones.some(
                      (c) => String(c.value) === String(formProg.Idcliente)
                    ) && (
                      <option value={formProg.Idcliente}>Cliente #{formProg.Idcliente}</option>
                    )}
                  {clientesOpciones.map((c) => (
                    <option key={`prog-cliente-${c.value}`} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Carguera</div>
                <select
                  name="idcarguera"
                  value={formProg.idcarguera}
                  onChange={handleProg}
                  style={fieldStyle}
                >
                  <option value="">-- Selecciona carguera --</option>
                  {formProg.idcarguera &&
                    !carguerasOpciones.some(
                      (c) => String(c.value) === String(formProg.idcarguera)
                    ) && (
                      <option value={formProg.idcarguera}>Carguera #{formProg.idcarguera}</option>
                    )}
                  {carguerasOpciones.map((c) => (
                    <option key={`prog-carguera-${c.value}`} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Día de la semana</div>
                <select
                  name="dia_semana"
                  value={formProg.dia_semana}
                  onChange={handleProg}
                  style={fieldStyle}
                >
                  {DOW_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Frecuencia</div>
                <select
                  name="frecuencia"
                  value={formProg.frecuencia}
                  onChange={handleProg}
                  style={fieldStyle}
                >
                  {FREQ_OPTS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Fecha inicio (opcional)</div>
                <input
                  name="fecha_inicio"
                  type="date"
                  value={formProg.fecha_inicio}
                  onChange={handleProg}
                  style={fieldStyle}
                />
              </label>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Fecha fin (opcional)</div>
                <input
                  name="fecha_fin"
                  type="date"
                  value={formProg.fecha_fin}
                  onChange={handleProg}
                  style={fieldStyle}
                />
              </label>
              <label>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Lead time (días)</div>
                <input
                  name="lead_time_dias"
                  type="number"
                  min={0}
                  value={formProg.lead_time_dias}
                  onChange={handleProg}
                  style={fieldStyle}
                />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Observaciones</div>
                <textarea
                  name="observaciones"
                  rows={3}
                  value={formProg.observaciones}
                  onChange={handleProg}
                  style={{ ...fieldStyle, minHeight: 96, resize: 'vertical' }}
                />
              </label>
            </div>
            <div
              style={{
                marginTop: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 16,
                flexWrap: 'wrap'
              }}
            >
              <div style={{ color: '#6b7280', fontSize: 12 }}>
                <div>
                  • <b>Lead time</b>: el cron de “lead_time” generará con días de anticipación.
                </div>
                <div>• El cron de “hoy” ignora este valor y genera el mismo día.</div>
              </div>
              <div style={actionsGroup}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{ ...actionButton, background: '#3b8dbd', color: '#fff' }}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={disabledProg}
                  style={{
                    ...actionButton,
                    background: '#2563eb',
                    color: '#fff',
                    minWidth: 170
                  }}
                >
                  {loading ? 'Guardando...' : 'Guardar programación'}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* ---------- SUBIR ---------- */}
        {tab === 'subir' && (
          <form onSubmit={submitSubir} style={{ marginTop: 14 }}>
            <div style={{ color: '#475569', fontSize: 13, marginBottom: 10 }}>
              Se crearán encabezados y detalle para las plantillas activas cuyo día coincida con la
              fecha seleccionada.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ flex: '0 0 240px' }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>Fecha (vuelo/embarque)</div>
                <input
                  type="date"
                  value={fechaSubir}
                  onChange={(e) => setFechaSubir(e.target.value)}
                  style={{ width: '100%', padding: 8 }}
                />
              </label>
              <button
                type="button"
                onClick={() => fetchPreview(fechaSubir)}
                disabled={preLoading}
                style={{ padding: '8px 14px' }}
              >
                {preLoading ? 'Actualizando...' : 'Actualizar lista'}
              </button>
              <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 12 }}>
                {selectedSubirCount} seleccionada(s) de {previewItems.length} para{' '}
                {niceDate(fechaSubir)}
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                maxHeight: 260,
                overflowY: 'auto'
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: '#f8fafc', color: '#0f172a' }}>
                  <tr>
                    <th
                      style={{
                        textAlign: 'center',
                        padding: '4px 8px',
                        borderBottom: '1px solid #e5e7eb',
                        width: 42
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allSubirSelected}
                        disabled={previewItems.length === 0 || preLoading}
                        onChange={(e) => toggleAllSubir(e.target.checked)}
                        title="Seleccionar todas"
                      />
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Cliente
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Observaciones
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Día
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Frecuencia
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Vigencia
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Creado
                    </th>
                    <th
                      style={{
                        textAlign: 'right',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Líneas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewItems.map((r) => {
                    const checked = selSubir.has(Number(r.id));
                    return (
                    <tr
                      key={r.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: checked ? '#ecfdf5' : '#fff'
                      }}
                    >
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleSubir(r.id, e.target.checked)}
                          title="Seleccionar orden fija"
                        />
                      </td>
                      <td style={{ padding: '8px 10px' }}>{r.id}</td>
                      <td style={{ padding: '8px 10px' }}>{r.cliente || r.Idcliente}</td>
                      <td
                        style={{
                          padding: '8px 10px',
                          maxWidth: 220,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={r.observaciones || ''}
                      >
                        {r.observaciones || '—'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {DOW_OPTS.find((d) => d.value === Number(r.dia_semana))?.label ||
                          r.dia_semana}
                      </td>
                      <td style={{ padding: '8px 10px' }}>{r.frecuencia}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {r.fecha_inicio ? niceDate(r.fecha_inicio) : '—'} →{' '}
                        {r.fecha_fin ? niceDate(r.fecha_fin) : '—'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>{niceDate(r.created_at)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.lineas ?? '—'}</td>
                    </tr>
                    );
                  })}
                  {(!preview?.items || preview.items.length === 0) && (
                    <tr>
                      <td
                        colSpan={9}
                        style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}
                      >
                        {preLoading ? 'Cargando...' : 'No hay programaciones para esta fecha.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {resultadoSubir && (
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 13
                }}
              >
                <div>
                  <b>OK:</b> {String(resultadoSubir.ok)}
                </div>
                {'generados' in resultadoSubir && (
                  <div>Detalles generados: {resultadoSubir.generados}</div>
                )}
                {'plantillas' in resultadoSubir && (
                  <div>Plantillas aplicadas: {resultadoSubir.plantillas}</div>
                )}
                {resultadoSubir?.headers?.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontWeight: 600 }}>Facturas creadas:</div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {resultadoSubir.headers.map((h, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          orden_fija_id: <b>{h.orden_fija_id}</b> → idfactura:{' '}
                          <button
                            type="button"
                            onClick={() => onOpenFactura?.(h.idfactura)}
                            style={{
                              border: 0,
                              background: 'transparent',
                              color: '#2563eb',
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                          >
                            {h.idfactura}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={onClose}
                style={{ padding: '8px 14px' }}
                disabled={loading}
              >
                Cerrar
              </button>
              <button
                type="submit"
                style={{
                  padding: '8px 14px',
                  background: '#059669',
                  color: '#fff',
                  border: 0,
                  borderRadius: 8
                }}
                disabled={loading || preLoading || selectedSubirCount === 0}
              >
                {loading ? 'Procesando...' : 'Subir seleccionadas'}
              </button>
            </div>
          </form>
        )}

        {/* ---------- GESTIONAR ---------- */}
        {tab === 'gestionar' && (
          <div style={{ marginTop: 14 }}>
            {/* PLANTILLAS */}
            <div style={sectionHeader}>
              <h4 style={{ margin: 0 }}>Plantillas</h4>
              <div style={actionsGroup}>
                <input
                  value={searchPl}
                  onChange={(e) => setSearchPl(e.target.value)}
                  placeholder="Buscar cliente..."
                  style={{ ...fieldStyle, width: 360 }}
                />
                <button
                  onClick={fetchPlantillas}
                  disabled={plLoading}
                  style={{ ...actionButton, background: '#3b8dbd', color: '#fff' }}
                >
                  {plLoading ? 'Actualizando...' : 'Refrescar'}
                </button>
                <button
                  onClick={verPdfPlantilla}
                  disabled={selPlantillas.size !== 1}
                  title="Ver PDF de la orden fija"
                  style={{ ...actionButton, background: '#3b8dbd', color: '#fff' }}
                >
                  PDF
                </button>
                <button
                  onClick={abrirEditarPlantilla}
                  disabled={selPlantillas.size !== 1}
                  title="Editar encabezado y detalle de la orden fija"
                  style={{
                    ...actionButton,
                    background: selPlantillas.size === 1 ? '#2563eb' : '#e5e7eb',
                    color: selPlantillas.size === 1 ? '#fff' : '#6b7280',
                    minWidth: 104
                  }}
                >
                  Editar
                </button>
                <button
                  onClick={eliminarPlantillas}
                  disabled={selPlantillas.size === 0}
                  style={{
                    ...actionButton,
                    background: '#b91c1c',
                    color: '#fff',
                    minWidth: 150
                  }}
                >
                  Eliminar orden fija
                </button>
              </div>
            </div>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                maxHeight: 220,
                overflowY: 'auto',
                marginBottom: 14
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelPlantillas(new Set(filtroPl.map((p) => p.id)));
                          else setSelPlantillas(new Set());
                        }}
                      />
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      ID
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Cliente
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Observaciones
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Día
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Frecuencia
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Vigencia
                    </th>
                    <th
                      style={{
                        textAlign: 'right',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Líneas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtroPl.map((p) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px' }}>
                        <input
                          type="checkbox"
                          checked={selPlantillas.has(p.id)}
                          onChange={(e) => {
                            const s = new Set(selPlantillas);
                            if (e.target.checked) s.add(p.id);
                            else s.delete(p.id);
                            setSelPlantillas(s);
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 10px' }}>{p.id}</td>
                      <td style={{ padding: '8px 10px' }}>{p.cliente || p.Idcliente}</td>
                      <td
                        style={{
                          padding: '8px 10px',
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={p.observaciones || ''}
                      >
                        {p.observaciones || '—'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {DOW_OPTS.find((d) => d.value === Number(p.dia_semana))?.label ||
                          p.dia_semana}
                      </td>
                      <td style={{ padding: '8px 10px' }}>{p.frecuencia}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {p.fecha_inicio ? niceDate(p.fecha_inicio) : '—'} →{' '}
                        {p.fecha_fin ? niceDate(p.fecha_fin) : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{p.lineas ?? '—'}</td>
                    </tr>
                  ))}
                  {filtroPl.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}
                      >
                        {plLoading ? 'Cargando...' : 'No hay plantillas.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PROGRAMACIONES (JOBS) */}
            <div style={sectionHeader}>
              <h4 style={{ margin: 0 }}>Programaciones generadas</h4>
              <div style={actionsGroup}>
                <input
                  value={searchJobs}
                  onChange={(e) => setSearchJobs(e.target.value)}
                  placeholder="Buscar cliente..."
                  style={{ ...fieldStyle, width: 360 }}
                />
                <button
                  onClick={fetchJobs}
                  disabled={jobsLoading}
                  style={{ ...actionButton, background: '#3b8dbd', color: '#fff' }}
                >
                  {jobsLoading ? 'Actualizando...' : 'Refrescar'}
                </button>

                <button
                  onClick={eliminarProgramaciones}
                  disabled={selJobs.size === 0}
                  style={{
                    ...actionButton,
                    background: '#b91c1c',
                    color: '#fff',
                    minWidth: 170
                  }}
                >
                  Eliminar programación
                </button>
              </div>
            </div>

            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                maxHeight: 220,
                overflowY: 'auto'
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) setSelJobs(new Set(filtroJobs.map((j) => j.id)));
                          else setSelJobs(new Set());
                        }}
                      />
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      JobID
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Plantilla
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Cliente
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Observaciones
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Fecha envío
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Estado
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      Creado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtroJobs.map((j) => (
                    <tr key={j.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px' }}>
                        <input
                          type="checkbox"
                          checked={selJobs.has(j.id)}
                          onChange={(e) => {
                            const s = new Set(selJobs);
                            if (e.target.checked) s.add(j.id);
                            else s.delete(j.id);
                            setSelJobs(s);
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 10px' }}>{j.id}</td>
                      <td style={{ padding: '8px 10px' }}>{j.orden_fija_id}</td>
                      <td style={{ padding: '8px 10px' }}>{j.cliente || j.Idcliente}</td>
                      <td
                        style={{
                          padding: '8px 10px',
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={j.observaciones || ''}
                      >
                        {j.observaciones || '—'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>{niceDate(j.fecha_envio)}</td>
                      <td style={{ padding: '8px 10px' }}>{j.estado}</td>
                      <td style={{ padding: '8px 10px' }}>{niceDate(j.created_at)}</td>
                    </tr>
                  ))}
                  {filtroJobs.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}
                      >
                        {jobsLoading ? 'Cargando...' : 'No hay programaciones.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ---------- MODAL EDITAR ORDEN FIJA ---------- */}
        {editOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, .55)',
              zIndex: 10020,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18
            }}
            onMouseDown={(e) => e.target === e.currentTarget && cerrarEditarPlantilla()}
          >
            <div
              style={{
                width: 'min(1680px, 98vw)',
                height: 'min(900px, 96vh)',
                background: '#fff',
                borderRadius: 14,
                boxShadow: '0 18px 50px rgba(0,0,0,.28)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  padding: '14px 16px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                    Editar orden fija {editHeader?.id ? `#${editHeader.id}` : ''}
                  </h3>
                  <div style={{ color: '#64748b', fontSize: 12 }}>
                    Edita encabezado, vigencia y cuerpo de la plantilla.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={cerrarEditarPlantilla}
                  style={{ border: 0, background: 'transparent', fontSize: 20, cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {editLoading ? (
                <div style={{ padding: 28, textAlign: 'center', color: '#64748b' }}>
                  Cargando orden fija...
                </div>
              ) : (
                <>
                  <div style={{ padding: 14, borderBottom: '1px solid #e5e7eb' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                        gap: 12,
                        alignItems: 'end'
                      }}
                    >
                      <label style={{ gridColumn: 'span 3' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Cliente</div>
                        <select
                          name="Idcliente"
                          value={editHeader?.Idcliente || ''}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        >
                          <option value="">-- Selecciona cliente --</option>
                          {editHeader?.Idcliente &&
                            !clientesOpciones.some(
                              (c) => String(c.value) === String(editHeader.Idcliente)
                            ) && (
                              <option value={editHeader.Idcliente}>
                                {editHeader.cliente || `Cliente #${editHeader.Idcliente}`}
                              </option>
                            )}
                          {clientesOpciones.map((c) => (
                            <option key={`edit-cliente-${c.value}`} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ gridColumn: 'span 3' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Carguera</div>
                        <select
                          name="idcarguera"
                          value={editHeader?.idcarguera || ''}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        >
                          <option value="">-- Selecciona carguera --</option>
                          {editHeader?.idcarguera &&
                            !carguerasOpciones.some(
                              (c) => String(c.value) === String(editHeader.idcarguera)
                            ) && (
                              <option value={editHeader.idcarguera}>
                                {editHeader.carguera || `Carguera #${editHeader.idcarguera}`}
                              </option>
                            )}
                          {carguerasOpciones.map((c) => (
                            <option key={`edit-carguera-${c.value}`} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ gridColumn: 'span 2' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Día</div>
                        <select
                          name="dia_semana"
                          value={editHeader?.dia_semana || 1}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        >
                          {DOW_OPTS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ gridColumn: 'span 2' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Frecuencia</div>
                        <select
                          name="frecuencia"
                          value={editHeader?.frecuencia || 'SEMANAL'}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        >
                          {FREQ_OPTS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ gridColumn: 'span 2' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Fecha inicio</div>
                        <input
                          name="fecha_inicio"
                          type="date"
                          value={editHeader?.fecha_inicio || ''}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        />
                      </label>
                      <label style={{ gridColumn: 'span 2' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Fecha fin</div>
                        <input
                          name="fecha_fin"
                          type="date"
                          value={editHeader?.fecha_fin || ''}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        />
                      </label>
                      <label style={{ gridColumn: 'span 2' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Lead time</div>
                        <input
                          name="lead_time_dias"
                          type="number"
                          min={0}
                          value={editHeader?.lead_time_dias ?? 0}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        />
                      </label>
                      <label style={{ gridColumn: 'span 2' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Estado</div>
                        <select
                          name="estado"
                          value={editHeader?.estado || 'proceso'}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                        >
                          <option value="proceso">proceso</option>
                          <option value="inactivo">inactivo</option>
                        </select>
                      </label>
                      <label style={{ gridColumn: 'span 4' }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>Observaciones</div>
                        <input
                          name="observaciones"
                          value={editHeader?.observaciones || ''}
                          onChange={handleEditHeader}
                          style={fieldStyle}
                          placeholder="Observaciones de la orden fija"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={guardarEncabezadoEdit}
                        disabled={editSavingHeader}
                        style={{
                          ...actionButton,
                          background: '#2563eb',
                          color: '#fff',
                          fontWeight: 700,
                          gridColumn: 'span 2'
                        }}
                      >
                        {editSavingHeader ? 'Guardando...' : 'Guardar encabezado'}
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '10px 14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                      borderBottom: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>Detalle de la orden fija</div>
                    <div style={actionsGroup}>
                      <button
                        type="button"
                        onClick={agregarDetalleEdit}
                        style={{
                          ...actionButton,
                          background: '#059669',
                          color: '#fff',
                          minWidth: 140
                        }}
                      >
                        + Agregar línea
                      </button>
                      <button
                        type="button"
                        onClick={eliminarDetalleEdit}
                        disabled={!editSelection.length}
                        style={{
                          ...actionButton,
                          background: '#b91c1c',
                          color: '#fff',
                          minWidth: 130
                        }}
                      >
                        Eliminar línea
                      </button>
                    </div>
                  </div>

                  <div style={{ flex: 1, minHeight: 0, padding: 12 }}>
                    <DataGridPremium
                      rows={editRows}
                      columns={columnsEditDetalle}
                      getRowId={getDetalleRowId}
                      checkboxSelection
                      disableRowSelectionOnClick
                      rowSelectionModel={editRowSelectionModel}
                      onRowSelectionModelChange={handleEditSelectionChange}
                      processRowUpdate={processEditRowUpdate}
                      onProcessRowUpdateError={(err) => {
                        console.error(err);
                        alert(
                          err?.response?.data?.msg || err.message || 'Error al guardar la línea'
                        );
                      }}
                      density="compact"
                      hideFooterSelectedRowCount
                      sx={{
                        border: '1px solid #e5e7eb',
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#f8fafc',
                          fontWeight: 800
                        }
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
